import { FlexCol } from '@/components/common/Flex';
import Modal from '@/components/common/modals/Modal';
import { Paragraph } from '@/components/common/Typography';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { isDev } from '@/util/env';
import { Center } from '@chakra-ui/react';
import { isObject } from '@common/util/types';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Button } from './Button';
import { PlentyLogo, RefreshCCW01 } from './icons';

interface VersionCheckerProps {
    children: ReactNode;
}

export const VersionChecker = ({
    children,
}: VersionCheckerProps): JSX.Element => {
    // Disable in dev; just adds noise.
    if (isDev) return <>{children}</>;

    return <VersionCheckerInner>{children}</VersionCheckerInner>;
};

const VersionCheckerInner = ({
    children,
}: VersionCheckerProps): JSX.Element => {
    const [isOutdated, setIsOutdated] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);

    const checkVersion = useCallback(() => {
        if (isOutdated) return;

        fetch(`${process.env.NEXT_PUBLIC_PLENTY_API_URL}/public/version`)
            .then((res) => res.json())
            .then((obj) => {
                if (
                    isObject(obj) &&
                    'minimumWebBuiltAt' in obj &&
                    typeof obj.minimumWebBuiltAt === 'string' &&
                    obj.minimumWebBuiltAt.length === 24 &&
                    typeof process.env.BUILT_AT === 'string' &&
                    process.env.BUILT_AT.length === 24 &&
                    obj.minimumWebBuiltAt > process.env.BUILT_AT
                ) {
                    setIsOutdated(true);
                    showOutdatedModal({ onClose: () => setIsDismissed(true) });
                }
            });
    }, [isOutdated]);

    useEffect(() => {
        checkVersion(); // run immediately on mount
        const interval = setInterval(checkVersion, 10000); // run every 10s
        return () => clearInterval(interval);
    }, [checkVersion, isDismissed]);

    return <>{children}</>;
};

export interface OutdatedModalProps {
    onClose: VoidFunction;
}

export const showOutdatedModal = (props: OutdatedModalProps) =>
    ModalManager.show(OutdatedModal, props);

const OutdatedModal = ModalManager.create<OutdatedModalProps>(({ onClose }) => {
    useTrackModalShown('Outdated Version');

    const modal = useModal();

    return (
        <Modal
            size="full"
            variant="unstyled"
            modalContentProps={{ bg: 'whiteAlpha.800', h: 'full' }}
            isOpen={modal.visible}
            autoFocus={false}
            onClose={() => {
                onClose();
                modal.hide();
            }}
            renderContent={() => {
                return (
                    <Center h="full">
                        <FlexCol alignItems="center" gap={6}>
                            <PlentyLogo w={16} h={16} />
                            <Paragraph fontWeight="semibold">
                                A new version of Plenty is available
                            </Paragraph>
                            <Button
                                leftIcon={
                                    <RefreshCCW01
                                        style={{ transform: 'scaleX(-1)' }}
                                    />
                                }
                                colorScheme="blackAlpha"
                                bg="text"
                                color="white"
                                fontSize="md"
                                size="sm"
                                onClick={() => window.location.reload()}
                                onClickEvent="Refresh Page Button Clicked"
                            >
                                Refresh page
                            </Button>
                        </FlexCol>
                    </Center>
                );
            }}
        ></Modal>
    );
});
